// Direct-manipulation timeline for the time entry edit form.
//
// Rides on the same <form> element as time-entry-edit. Renders the entry as
// a horizontal bar with a drag handle at each end (trim) and tap-to-split:
// tapping the bar drops a divider, revealing a second-entry card whose task
// the tail of the split will be posted to (`split_at` + `split_task_id`).
// Tapping again relocates the divider; dragging it off either end — or the
// card's "Remove split" button — dissolves the split. A ghost bar shows the
// entry's original extent, and the user's neighboring entries render as
// dimmed segments whose EXACT boundaries (seconds included) the handles
// snap to magnetically — that exactness is what keeps back-to-back entries
// overlap-free (see the guards in TimeEntry's virtual time setters).
//
// Nothing here persists anything: handles write into the form's
// started_time / ended_time text inputs (seconds included only when a snap
// carries them) and the split fields — live while dragging, not just on
// release — and Save submits as usual.
//
// Times: all internal state is "wall-clock ms" in the SERVER's time zone —
// utc ms + the utc offset carried by the ISO values — because the form's
// text fields are server-zone wall clock. Browser-local time is never used.
import { Controller } from "@hotwired/stimulus"
import { parseTimeOfDay } from "../lib/time_of_day"

const MINUTE = 60_000
const HOUR = 3_600_000
const SNAP_PX = 8       // magnet radius toward neighbor boundaries
const TAP_SLOP_PX = 6   // pointer movement allowed for a "tap"
const NEIGHBOR_WINDOW = 2 * HOUR // ignore neighbors further out than this

export default class extends Controller<HTMLFormElement> {
  static targets = [
    "track", "ticks", "ghost", "seg1", "seg2",
    "prevSeg", "prevSegLabel", "nextSeg", "nextSegLabel",
    "handleStart", "handleEnd", "divider", "dividerLabel",
    "labelStart", "labelEnd", "hint",
    "splitCard", "splitAtField", "splitReadout",
    "splitCustomer", "splitProject", "splitTask",
  ]
  static values = {
    start: String, end: String,
    prevStart: String, prevEnd: String, prevLabel: String,
    nextStart: String, nextEnd: String, nextLabel: String,
    splittable: { type: Boolean, default: true },
  }

  declare readonly trackTarget: HTMLElement
  declare readonly ticksTarget: HTMLElement
  declare readonly ghostTarget: HTMLElement
  declare readonly seg1Target: HTMLElement
  declare readonly seg2Target: HTMLElement
  declare readonly prevSegTarget: HTMLElement
  declare readonly prevSegLabelTarget: HTMLElement
  declare readonly nextSegTarget: HTMLElement
  declare readonly nextSegLabelTarget: HTMLElement
  declare readonly handleStartTarget: HTMLElement
  declare readonly handleEndTarget: HTMLElement
  declare readonly dividerTarget: HTMLElement
  declare readonly dividerLabelTarget: HTMLElement
  declare readonly labelStartTarget: HTMLElement
  declare readonly labelEndTarget: HTMLElement
  declare readonly hintTarget: HTMLElement
  declare readonly splitCardTarget: HTMLElement
  declare readonly splitAtFieldTarget: HTMLInputElement
  declare readonly splitReadoutTarget: HTMLElement
  declare readonly splitCustomerTarget: HTMLSelectElement
  declare readonly splitProjectTarget: HTMLSelectElement
  declare readonly splitTaskTarget: HTMLSelectElement
  declare readonly hasPrevSegTarget: boolean
  declare readonly hasNextSegTarget: boolean

  declare readonly startValue: string
  declare readonly endValue: string
  declare readonly prevStartValue: string
  declare readonly prevEndValue: string
  declare readonly prevLabelValue: string
  declare readonly nextStartValue: string
  declare readonly nextEndValue: string
  declare readonly nextLabelValue: string
  declare readonly splittableValue: boolean

  // Wall-clock ms state
  private offset = 0            // server-zone utc offset in ms
  private start0 = 0            // original extent (ghost)
  private end0 = 0
  private start = 0             // current extent
  private end = 0
  private splitAt: number | null = null
  private w0 = 0                // rendered window
  private w1 = 0
  private prevStart: number | null = null
  private prevEnd: number | null = null
  private nextStart: number | null = null
  private nextEnd: number | null = null

  private dragging: "start" | "end" | "divider" | null = null
  private dividerDoomed = false // dragged off the end: release removes it
  private tapDownX: number | null = null
  private tapDownY = 0
  private syncing = false       // guards the input-field feedback loop
  private resizeObserver: ResizeObserver | null = null

  private startedInput!: HTMLInputElement
  private endedInput!: HTMLInputElement
  private hoursInput!: HTMLInputElement

  connect(): void {
    this.offset = this.isoOffsetMs(this.startValue)
    this.start0 = this.start = this.wallMs(this.startValue)
    this.end0 = this.end = this.wallMs(this.endValue)
    if (this.prevStartValue) {
      this.prevStart = this.wallMs(this.prevStartValue)
      this.prevEnd = this.wallMs(this.prevEndValue)
    }
    if (this.nextStartValue) {
      this.nextStart = this.wallMs(this.nextStartValue)
      this.nextEnd = this.wallMs(this.nextEndValue)
    }
    this.computeWindow()

    const form = this.element
    this.startedInput = form.querySelector('input[name="time_entry[started_time]"]') as HTMLInputElement
    this.endedInput = form.querySelector('input[name="time_entry[ended_time]"]') as HTMLInputElement
    this.hoursInput = form.querySelector('input[name="time_entry[hours]"]') as HTMLInputElement
    this.startedInput?.addEventListener("input", this.onManualTimeInput)
    this.endedInput?.addEventListener("input", this.onManualTimeInput)

    this.trackTarget.addEventListener("pointerdown", this.onPointerDown)
    this.trackTarget.addEventListener("pointermove", this.onPointerMove)
    this.trackTarget.addEventListener("pointerup", this.onPointerUp)
    this.trackTarget.addEventListener("pointercancel", this.onPointerUp)
    this.handleStartTarget.addEventListener("keydown", e => this.onHandleKey(e, "start"))
    this.handleEndTarget.addEventListener("keydown", e => this.onHandleKey(e, "end"))
    this.dividerTarget.addEventListener("keydown", e => this.onHandleKey(e, "divider"))

    // Tick density depends on pixel width — re-render when it changes.
    this.resizeObserver = new ResizeObserver(() => this.renderTicks())
    this.resizeObserver.observe(this.trackTarget)

    this.render()
  }

  disconnect(): void {
    this.startedInput?.removeEventListener("input", this.onManualTimeInput)
    this.endedInput?.removeEventListener("input", this.onManualTimeInput)
    this.resizeObserver?.disconnect()
  }

  // ---- Time plumbing -------------------------------------------------------

  // "2001-01-01T13:01:01-08:00" → -8h in ms. "Z" → 0.
  private isoOffsetMs(iso: string): number {
    const m = /(Z|[+-]\d{2}:\d{2})$/.exec(iso)
    if (!m || m[1] === "Z") return 0
    const sign = m[1][0] === "-" ? -1 : 1
    const [h, min] = m[1].slice(1).split(":").map(Number)
    return sign * (h * HOUR + min * MINUTE)
  }

  private wallMs(iso: string): number {
    return Date.parse(iso) + this.offset
  }

  private wallToIso(wall: number): string {
    return new Date(wall - this.offset).toISOString()
  }

  private parts(wall: number): { h: number; m: number; s: number } {
    const d = new Date(wall)
    return { h: d.getUTCHours(), m: d.getUTCMinutes(), s: d.getUTCSeconds() }
  }

  // Value for the form's time text inputs: 24h "HH:MM", with ":SS" only
  // when a snap carried exact seconds (the model honors seconds-bearing
  // input; minute-precision input matching the stored minute is a no-op).
  private fmtInput(wall: number): string {
    const { h, m, s } = this.parts(wall)
    const base = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
    return s ? `${base}:${String(s).padStart(2, "0")}` : base
  }

  // Human label: "2:32 pm" (seconds shown only when nonzero: "2:32:41 pm").
  private fmtLabel(wall: number): string {
    const { h, m, s } = this.parts(wall)
    const ap = h < 12 ? "am" : "pm"
    let hh = h % 12
    if (hh === 0) hh = 12
    const sec = s ? `:${String(s).padStart(2, "0")}` : ""
    return `${hh}:${String(m).padStart(2, "0")}${sec} ${ap}`
  }

  private fmtHours(ms: number): string {
    return (ms / HOUR).toFixed(3)
  }

  // ---- Geometry ------------------------------------------------------------

  private computeWindow(): void {
    let lo = this.start0
    let hi = this.end0
    if (this.prevEnd !== null && this.start0 - this.prevEnd <= NEIGHBOR_WINDOW) lo = Math.min(lo, this.prevEnd)
    if (this.nextStart !== null && this.nextStart - this.end0 <= NEIGHBOR_WINDOW) hi = Math.max(hi, this.nextStart)
    const pad = Math.max(15 * MINUTE, 0.15 * (this.end0 - this.start0))
    this.w0 = lo - pad
    this.w1 = hi + pad
  }

  private pct(wall: number): number {
    return ((wall - this.w0) / (this.w1 - this.w0)) * 100
  }

  private wallAtClientX(clientX: number): number {
    const rect = this.trackTarget.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    return this.w0 + frac * (this.w1 - this.w0)
  }

  private pxToMs(px: number): number {
    return (px / this.trackTarget.getBoundingClientRect().width) * (this.w1 - this.w0)
  }

  // ---- Rendering -----------------------------------------------------------

  private render(): void {
    const seg = (el: HTMLElement, a: number, b: number) => {
      el.style.left = `${this.pct(a)}%`
      el.style.width = `${Math.max(0, this.pct(b) - this.pct(a))}%`
    }

    seg(this.ghostTarget, this.start0, this.end0)
    seg(this.seg1Target, this.start, this.splitAt ?? this.end)

    if (this.splitAt !== null) {
      this.seg2Target.classList.remove("hidden")
      seg(this.seg2Target, this.splitAt, this.end)
      this.dividerTarget.classList.remove("hidden")
      this.dividerTarget.style.left = `${this.pct(this.splitAt)}%`
      this.dividerLabelTarget.textContent = this.dividerDoomed ? "release to remove" : this.fmtLabel(this.splitAt)
      this.dividerTarget.classList.toggle("opacity-40", this.dividerDoomed)
    } else {
      this.seg2Target.classList.add("hidden")
      this.dividerTarget.classList.add("hidden")
    }

    if (this.prevStart !== null && this.prevEnd !== null && this.prevEnd > this.w0) {
      this.prevSegTarget.classList.remove("hidden")
      seg(this.prevSegTarget, Math.max(this.prevStart, this.w0), this.prevEnd)
      this.prevSegLabelTarget.textContent = this.prevLabelValue
      this.prevSegTarget.title = `${this.prevLabelValue} · ends ${this.fmtLabel(this.prevEnd)}`
    } else if (this.hasPrevSegTarget) {
      this.prevSegTarget.classList.add("hidden")
    }
    if (this.nextStart !== null && this.nextEnd !== null && this.nextStart < this.w1) {
      this.nextSegTarget.classList.remove("hidden")
      seg(this.nextSegTarget, this.nextStart, Math.min(this.nextEnd, this.w1))
      this.nextSegLabelTarget.textContent = this.nextLabelValue
      this.nextSegTarget.title = `${this.nextLabelValue} · starts ${this.fmtLabel(this.nextStart)}`
    } else if (this.hasNextSegTarget) {
      this.nextSegTarget.classList.add("hidden")
    }

    this.handleStartTarget.style.left = `${this.pct(this.start)}%`
    this.handleEndTarget.style.left = `${this.pct(this.end)}%`

    this.labelStartTarget.style.left = `${Math.min(97, Math.max(3, this.pct(this.start)))}%`
    this.labelStartTarget.textContent = this.fmtLabel(this.start)
    this.labelEndTarget.style.left = `${Math.min(97, Math.max(3, this.pct(this.end)))}%`
    this.labelEndTarget.textContent = this.fmtLabel(this.end)

    this.renderSplitCard()
    this.renderTicks()
  }

  private renderSplitCard(): void {
    const active = this.splitAt !== null
    this.splitCardTarget.classList.toggle("hidden", !active)
    this.splitAtFieldTarget.disabled = !active
    this.splitTaskTarget.disabled = !active
    if (active) {
      this.splitAtFieldTarget.value = this.wallToIso(this.splitAt!)
      this.splitReadoutTarget.textContent =
        `${this.fmtLabel(this.splitAt!)} – ${this.fmtLabel(this.end)} · ${this.fmtHours(this.end - this.splitAt!)}h` +
        ` (first entry keeps ${this.fmtLabel(this.start)} – ${this.fmtLabel(this.splitAt!)} · ${this.fmtHours(this.splitAt! - this.start)}h)`
      this.hintTarget.textContent = "Drag the divider to adjust · tap elsewhere to move it"
    } else {
      this.splitAtFieldTarget.value = ""
      this.hintTarget.textContent = this.splittableValue
        ? "Drag the ends to trim · tap the bar to split"
        : "Drag the ends to set the time"
    }
  }

  private renderTicks(): void {
    const width = this.trackTarget.clientWidth
    if (!width) return
    const pxPerHour = width / ((this.w1 - this.w0) / HOUR)
    // Label every 1/2/4 hours depending on density; always draw hour lines.
    const labelEvery = pxPerHour >= 48 ? 1 : pxPerHour >= 24 ? 2 : 4
    let html = ""
    const firstHour = Math.ceil(this.w0 / HOUR) * HOUR
    for (let t = firstHour; t <= this.w1; t += HOUR) {
      const left = this.pct(t)
      const hour = this.parts(t).h
      const showLabel = hour % labelEvery === 0
      html += `<div class="absolute top-0 bottom-0 w-px bg-white/[0.06]" style="left:${left}%"></div>`
      if (showLabel) {
        const ap = hour < 12 ? "am" : "pm"
        let hh = hour % 12
        if (hh === 0) hh = 12
        html += `<div class="absolute bottom-0.5 -translate-x-1/2 text-[8px] text-[var(--color-ink-dim)]" style="left:${left}%">${hh}${ap}</div>`
      }
    }
    this.ticksTarget.innerHTML = html
  }

  // ---- Dragging ------------------------------------------------------------

  private onPointerDown = (e: PointerEvent): void => {
    const target = e.target as HTMLElement
    if (this.handleStartTarget.contains(target)) this.dragging = "start"
    else if (this.handleEndTarget.contains(target)) this.dragging = "end"
    else if (!this.dividerTarget.classList.contains("hidden") && this.dividerTarget.contains(target)) this.dragging = "divider"
    else {
      // Potential tap-to-split / tap-to-move-divider.
      this.tapDownX = e.clientX
      this.tapDownY = e.clientY
    }
    if (this.dragging) {
      e.preventDefault()
      this.trackTarget.setPointerCapture(e.pointerId)
    }
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dragging) return
    e.preventDefault()
    const raw = this.wallAtClientX(e.clientX)
    const snapMs = this.pxToMs(SNAP_PX)

    if (this.dragging === "start") {
      let t = Math.round(raw / MINUTE) * MINUTE
      if (this.prevEnd !== null && Math.abs(raw - this.prevEnd) < snapMs) t = this.prevEnd
      const min = this.prevEnd !== null ? this.prevEnd : this.w0
      const max = (this.splitAt ?? this.end) - MINUTE
      this.start = Math.min(max, Math.max(min, t))
    } else if (this.dragging === "end") {
      let t = Math.round(raw / MINUTE) * MINUTE
      if (this.nextStart !== null && Math.abs(raw - this.nextStart) < snapMs) t = this.nextStart
      const max = this.nextStart !== null ? this.nextStart : this.w1
      const min = (this.splitAt ?? this.start) + MINUTE
      this.end = Math.max(min, Math.min(max, t))
    } else if (this.dragging === "divider") {
      const t = Math.round(raw / MINUTE) * MINUTE
      // Off either end: flag for removal on release.
      this.dividerDoomed = raw <= this.start + MINUTE / 2 || raw >= this.end - MINUTE / 2
      this.splitAt = Math.min(this.end - MINUTE, Math.max(this.start + MINUTE, t))
    }
    // Live-sync the time fields while the handle moves (writeFields is
    // cheap: it only touches inputs whose value actually changed, and the
    // `syncing` guard prevents the input listeners from looping back).
    this.writeFields()
    this.render()
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (this.dragging) {
      if (this.dragging === "divider" && this.dividerDoomed) {
        this.splitAt = null
        this.dividerDoomed = false
      }
      this.dragging = null
      this.writeFields()
      this.render()
      return
    }
    // Tap?
    if (this.tapDownX !== null &&
        Math.abs(e.clientX - this.tapDownX) <= TAP_SLOP_PX &&
        Math.abs(e.clientY - this.tapDownY) <= TAP_SLOP_PX) {
      this.placeSplit(this.wallAtClientX(e.clientX))
    }
    this.tapDownX = null
  }

  private onHandleKey(e: KeyboardEvent, which: "start" | "end" | "divider"): void {
    let delta = 0
    if (e.key === "ArrowLeft") delta = -MINUTE
    else if (e.key === "ArrowRight") delta = MINUTE
    else if ((e.key === "Delete" || e.key === "Backspace") && which === "divider") {
      e.preventDefault()
      this.removeSplit()
      return
    } else return
    if (e.shiftKey) delta *= 5
    e.preventDefault()

    if (which === "start") {
      const min = this.prevEnd !== null ? this.prevEnd : this.w0
      this.start = Math.min((this.splitAt ?? this.end) - MINUTE, Math.max(min, this.start + delta))
    } else if (which === "end") {
      const max = this.nextStart !== null ? this.nextStart : this.w1
      this.end = Math.max((this.splitAt ?? this.start) + MINUTE, Math.min(max, this.end + delta))
    } else if (this.splitAt !== null) {
      this.splitAt = Math.min(this.end - MINUTE, Math.max(this.start + MINUTE, this.splitAt + delta))
    }
    this.writeFields()
    this.render()
  }

  // ---- Split lifecycle -----------------------------------------------------

  private placeSplit(rawWall: number): void {
    // Nothing to split before the entry exists (new-entry form).
    if (!this.splittableValue) return
    // Too short to split (need ≥1 minute on each side).
    if (this.end - this.start < 2 * MINUTE) return
    // Only a tap ON the entry's bar splits it — a tap on empty track or a
    // neighbor would otherwise clamp to an edge and make a surprise
    // 1-minute sliver.
    if (rawWall <= this.start || rawWall >= this.end) return
    const t = Math.round(rawWall / MINUTE) * MINUTE
    this.splitAt = Math.min(this.end - MINUTE, Math.max(this.start + MINUTE, t))
    this.render()
    if (this.splitAt !== null) this.dividerTarget.focus?.()
  }

  removeSplit(): void {
    this.splitAt = null
    this.dividerDoomed = false
    this.render()
  }

  // ---- Form synchronization ------------------------------------------------

  // Push timeline state into the form's text inputs. The hours field is
  // display-only here — the model ignores it whenever the times changed.
  private writeFields(): void {
    this.syncing = true
    try {
      const fire = (el: HTMLInputElement, value: string) => {
        if (el && el.value !== value) {
          el.value = value
          el.dispatchEvent(new Event("input", { bubbles: true }))
        }
      }
      fire(this.startedInput, this.fmtInput(this.start))
      fire(this.endedInput, this.fmtInput(this.end))
      if (this.hoursInput) this.hoursInput.value = this.fmtHours(this.end - this.start)
    } finally {
      this.syncing = false
    }
  }

  // Typing in the time fields moves the bar too (one-way per direction,
  // guarded against loops). Invalid or inverted input just leaves the bar.
  private onManualTimeInput = (e: Event): void => {
    if (this.syncing) return
    const input = e.target as HTMLInputElement
    const parsed = parseTimeOfDay(input.value)
    if (!parsed.valid) return
    const day = Math.floor(this.start / (24 * HOUR)) * 24 * HOUR
    const t = day + parsed.hour * HOUR + parsed.minute * MINUTE + parsed.second * 1000
    if (input === this.startedInput) {
      if (t < (this.splitAt ?? this.end)) {
        this.start = t
        this.computeWindow()
        this.render()
      }
    } else if (input === this.endedInput) {
      if (t > (this.splitAt ?? this.start)) {
        this.end = t
        this.computeWindow()
        this.render()
      }
    }
  }

  // ---- Second-entry cascade (mirrors time-entry-edit's) ---------------------

  splitCustomerChanged(): void {
    const customerId = this.splitCustomerTarget.value
    if (!customerId || customerId === "A") return
    fetch(`/time_entries/projects?customer_id=${encodeURIComponent(customerId)}`, {
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
    })
      .then(r => r.text())
      .then(html => {
        this.splitProjectTarget.innerHTML = html
        this.dropPlaceholder(this.splitProjectTarget)
        this.splitProjectChanged()
      })
  }

  splitProjectChanged(): void {
    const projectId = this.splitProjectTarget.value
    if (!projectId || projectId === "A") {
      this.splitTaskTarget.innerHTML = ""
      return
    }
    fetch(`/time_entries/tasks?project_id=${encodeURIComponent(projectId)}`, {
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
    })
      .then(r => r.text())
      .then(html => {
        this.splitTaskTarget.innerHTML = html
        this.dropPlaceholder(this.splitTaskTarget)
      })
  }

  private dropPlaceholder(select: HTMLSelectElement): void {
    const first = select.options[0]
    if (first && first.value === "A") select.remove(0)
    if (select.options.length > 0) select.selectedIndex = 0
  }
}
